import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import type { Currency } from '../../api/stripe';
import { useTranslation } from 'react-i18next';
import { CabinetPreviewFrame } from '../../components/CabinetPreviewFrame/CabinetPreviewFrame';
import './index.css';
import { BookCover } from '../../components/BookCover/BookCover';
import { BookPage } from '../../components/BookPage/BookPage';
import { LangPicker } from './LangPicker';
import { FALLBACK_LANGUAGE } from '../../i18n/languages';
import { CheckoutSidebar } from '../../components/Checkout/CheckoutSidebar';
import { isAskVariant, isMirrorVariant, mainAppUrl } from '../../utils/variant';
import { trackLead } from '../../utils/pixel';

import heroVideo from '../../assets/landing/people/video.mp4';
import heroVideoPoster from '../../assets/landing/people/preview.jpg';
import p1 from '../../assets/landing/people/IMG_7248.PNG';
import p2 from '../../assets/landing/people/IMG_7249.PNG';
import p3 from '../../assets/landing/people/IMG_7251.PNG';
import p4 from '../../assets/landing/people/IMG_7252.PNG';
import p5 from '../../assets/landing/people/IMG_7254.PNG';
import p6 from '../../assets/landing/people/IMG_8434.PNG';
import p7 from '../../assets/landing/people/IMG_7256.PNG';
import p8 from '../../assets/landing/people/IMG_7257.PNG';
import p9 from '../../assets/landing/people/IMG_7258.PNG';
import p10 from '../../assets/landing/people/IMG_7259.PNG';
import p11 from '../../assets/landing/people/IMG_7260.PNG';
import p12 from '../../assets/landing/people/IMG_7261.PNG';
import p13 from '../../assets/landing/people/IMG_7262.PNG';
import p14 from '../../assets/landing/people/IMG_4432.JPG';
import p15 from '../../assets/landing/people/IMG_4758.PNG';

import npMom from '../../assets/landing/newpeople/маме.jpg';
import npBoyfriend from '../../assets/landing/newpeople/парню.jpg';
import npBoyfriend2 from '../../assets/landing/newpeople/парню2.jpg';
import npGirlfriend from '../../assets/landing/newpeople/девушке.jpg';
import npWife from '../../assets/landing/newpeople/жене.jpg';
import npHusband from '../../assets/landing/newpeople/мужу.jpg';
import npHusband2 from '../../assets/landing/newpeople/мужу2.jpg';
import npFriend from '../../assets/landing/newpeople/подруге.jpg';
import npSister from '../../assets/landing/newpeople/сестре.jpg';
import npGrandma from '../../assets/landing/newpeople/бабушке.jpg';

import cover1 from '../../assets/landing/cmock1.jpg';
import cover2 from '../../assets/landing/cmock2.jpg';
import cover3 from '../../assets/landing/cmock3.jpg';
import cover4 from '../../assets/landing/cmock4.jpg';
import cover5 from '../../assets/landing/cmock5.jpg';
import doodleArrow from '../../assets/landing/doodlearrow.svg';
import bookImage from '../../assets/landing/book.png';


// Cover image rotation list — title/author come from translations.
const bookCovers = [cover5, cover2, cover3, cover1, cover4];

// Hero grid photos. Each entry pairs with the doodle of the same index in
// `photoDoodles` — keep them in sync so each photo's hand-drawn label
// matches the recipient shown in the photo's filename.
const peopleImages = [
  npMom,        // маме
  npBoyfriend,  // парню
  npGirlfriend, // девушке
  npFriend,     // подруге
  npWife,       // жене
  npHusband,    // мужу
  npSister,     // сестре
  npGrandma,    // бабушке
  npBoyfriend2, // парню (2)
  npHusband2,   // мужу (2)
  npMom,        // маме
  npGirlfriend, // девушке
  npBoyfriend,  // парню
  p6,           // подруге (2) → IMG_8434.PNG
  npWife,       // жене
];

// Desktop: 5×3 grid around the video. Outer columns (±2) are shifted down
// by 0.3 step so they read as a staggered/brick pattern relative to the
// inner columns (±1). [0, 1] is omitted (would sit under the video).
// 13 photos.
const photoOffsetsDesktop: ReadonlyArray<readonly [number, number]> = [
  [-2, -0.7], [-1, -1], [0, -1], [1, -1], [2, -0.7],
  [-2,  0.3], [-1,  0],          [1,  0], [2,  0.3],
  [-2,  1.3], [-1,  1],          [1,  1], [2,  1.3],
];

// Mobile: 3 cols × 6 rows. Outer columns (±1) are shifted down by 0.3 step
// for a brick/staggered effect against the centre column. Centre column
// carries two photos at the top (rows −2.3 and −1.4, both above the video);
// no centre entries below the video.
const photoOffsetsMobile: ReadonlyArray<readonly [number, number]> = [
  [-1, -2.0], [0, -2.3], [1, -2.0],
  [-1, -1.1], [0, -1.4], [1, -1.1],
  [-1, -0.2],            [1, -0.2],
  [-1,  0.8],            [1,  0.8],
  [-1,  1.7],            [1,  1.7],
  [-1,  2.6],            [1,  2.6],
];

// Hand-drawn doodle placement per photo. The label TEXT is pulled from
// translations at render time using the doodleKey field.
type DoodlePlacement = 'tl' | 'tr' | 'bl' | 'br';
type DoodleKey =
  | 'mom'
  | 'boyfriend'
  | 'girlfriend'
  | 'wife'
  | 'husband'
  | 'friend'
  | 'sister'
  | 'grandma';
type Doodle = {
  doodleKey: DoodleKey;
  placement: DoodlePlacement;
  rotate: number;
};

const photoDoodles: ReadonlyArray<Doodle> = [
  { doodleKey: 'mom',        placement: 'tr', rotate: -4 },
  { doodleKey: 'boyfriend',  placement: 'bl', rotate:  3 },
  { doodleKey: 'girlfriend', placement: 'br', rotate: -2 },
  { doodleKey: 'friend',     placement: 'tl', rotate:  4 },
  { doodleKey: 'wife',       placement: 'br', rotate: -3 },
  { doodleKey: 'husband',    placement: 'tl', rotate:  2 },
  { doodleKey: 'sister',     placement: 'tr', rotate: -2 },
  { doodleKey: 'grandma',    placement: 'bl', rotate:  3 },
  { doodleKey: 'boyfriend',  placement: 'tl', rotate:  4 },
  { doodleKey: 'husband',    placement: 'br', rotate: -3 },
  { doodleKey: 'mom',        placement: 'tr', rotate:  2 },
  { doodleKey: 'girlfriend', placement: 'bl', rotate: -4 },
  { doodleKey: 'boyfriend',  placement: 'br', rotate:  3 },
  { doodleKey: 'friend',     placement: 'tl', rotate: -2 },
  { doodleKey: 'wife',       placement: 'tr', rotate:  4 },
];

// Review slot order — quote/by content for the three text reviews come
// from translations (reviews.r1Quote / r1By / r2Quote / ...).
type ReviewSlot =
  | { kind: 'photo'; poster: string }
  | { kind: 'text'; quoteKey: string; byKey: string };

const reviewSlots: ReadonlyArray<ReviewSlot> = [
  { kind: 'photo', poster: p1 },
  { kind: 'photo', poster: p3 },
  { kind: 'text',  quoteKey: 'reviews.r1Quote', byKey: 'reviews.r1By' },
  { kind: 'photo', poster: p7 },
  { kind: 'photo', poster: p9 },
  { kind: 'text',  quoteKey: 'reviews.r2Quote', byKey: 'reviews.r2By' },
  { kind: 'photo', poster: p11 },
  { kind: 'photo', poster: p13 },
  { kind: 'text',  quoteKey: 'reviews.r3Quote', byKey: 'reviews.r3By' },
  { kind: 'photo', poster: p15 },
];

const ctaOccasionKeys = [
  { wordKey: 'cta.birthdayWord',     msgKey: 'cta.birthdayMsg' },
  { wordKey: 'cta.anniversaryWord',  msgKey: 'cta.anniversaryMsg' },
  { wordKey: 'cta.mothersWord',      msgKey: 'cta.mothersMsg' },
  { wordKey: 'cta.holidayWord',      msgKey: 'cta.holidayMsg' },
];

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);
const clamp01 = (v: number) => Math.max(0, Math.min(1, v));

// На ask-зеркале все CTA ведут в Telegram-канал менеджера. Константа на
// случай, если адрес поменяется — поправить в одном месте.
const TELEGRAM_LINK = 'https://t.me/lovestoriabook';

// Telegram paper-plane glyph. Рисуется белым через currentColor.
const TelegramIcon: React.FC = () => (
  <svg
    className="lp-tg-mark"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M21.94 4.34a1.2 1.2 0 0 0-1.62-1.16L2.7 9.95c-1.07.4-1.06 1.05-.18 1.31l4.5 1.4 10.45-6.6c.49-.31.94-.14.57.2L9.6 13.97l-.33 4.86c.42 0 .6-.19.84-.42l2.02-1.96 4.2 3.1c.77.42 1.32.21 1.51-.7l2.74-12.78c.08-.39.05-.66-.04-.83Z" />
  </svg>
);

const Landing: React.FC = () => {
  const { t } = useTranslation('landing');
  const { lang: urlLang } = useParams<{ lang: string }>();
  const [searchParams] = useSearchParams();
  // ?curr=KZT — preselect a currency in the checkout sidebar (default USD).
  const SUPPORTED_CURRENCIES = ['USD', 'AZN', 'BYN', 'GEL', 'KZT', 'TRY'] as const;
  const currParam = searchParams.get('curr')?.toUpperCase();
  const defaultCurrency: Currency =
    currParam && (SUPPORTED_CURRENCIES as ReadonlyArray<string>).includes(currParam)
      ? (currParam as Currency)
      : 'USD';
  const langPrefix = '/' + (urlLang || FALLBACK_LANGUAGE);
  const heroRef = useRef<HTMLElement>(null);
  const [progress, setProgress] = useState(0);
  const [vp, setVp] = useState(() => ({
    w: typeof window === 'undefined' ? 1440 : window.innerWidth,
    h: typeof window === 'undefined' ? 900 : window.innerHeight,
  }));
  const [showAnno, setShowAnno] = useState(true);
  const [bookIdx, setBookIdx] = useState(0);
  const [occIdx, setOccIdx] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const navRef = useRef<HTMLElement>(null);

  // Close burger menu on outside click / Escape
  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  useEffect(() => {
    const id = setInterval(() => {
      setBookIdx((i) => (i + 1) % bookCovers.length);
    }, 3500);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const id = setInterval(() => {
      setOccIdx((i) => (i + 1) % ctaOccasionKeys.length);
    }, 3200);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    let ticking = false;
    const update = () => {
      ticking = false;
      const el = heroRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const scrollable = Math.max(rect.height * (5 / 6), 1);
      const scrolled = -rect.top;
      setProgress(clamp01(scrolled / scrollable));
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };
    const onResize = () => {
      // Mobile browsers fire `resize` when their address bar shows/hides,
      // which only changes height — re-running our height-based math then
      // makes the hero animation jump. Only refresh `vp.h` when the width
      // also changed (rotation / actual resize); otherwise keep the height
      // we measured on first paint so the animation stays stable.
      const newW = window.innerWidth;
      const newH = window.innerHeight;
      setVp((prev) => (prev.w === newW ? prev : { w: newW, h: newH }));
      onScroll();
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const isMobile = vp.w < 760;

  // Five scroll stages:
  //  • Phase 1 (0    → 0.30): entrance — photos/video appear, caption
  //    slides in to its lower slot.
  //  • Phase 2 (0.30 → 0.50): photos and video scroll up & out, caption
  //    rises to the viewport centre.
  //  • Hold    (0.50 → 0.55): brief pause at the centred state.
  //  • Phase 3 (0.55 → 0.80): caption continues rising into the upper area
  //    while the book preview slides in and rotates from a 3D pose to flat.
  //    Wider window than before so the rotation reads as deliberate.
  //  • Phase 4 (0.80 → 1.00): book stays put and its cover cycles through
  //    the example list. Only after this finishes does the hero hand off
  //    to the next section.
  const phase1 = clamp01(progress / 0.30);
  const phase2 = clamp01((progress - 0.30) / 0.20);
  const phase3 = clamp01((progress - 0.50) / 0.35);
  const phase4 = clamp01((progress - 0.75) / 0.25);
  const eased = easeOutCubic(phase1);

  // Photo edge length (square). Mobile is bumped up so the squares feel
  // substantial and naturally overflow the screen edges. Desktop cap is
  // small enough that the ±2 columns still fit on MacBook 1280/1440 widths.
  const photoSize = isMobile
    ? Math.min(vp.w * 0.26, 110)
    : Math.max(105, Math.min(vp.w * 0.09, vp.h * 0.18, 130));

  // Video shrinks to a vertical 9:16 rectangle, twice as wide as a photo.
  const videoFinalW = photoSize * 2;
  const videoFinalH = videoFinalW * (16 / 9);

  const videoW = lerp(vp.w, videoFinalW, eased);
  const videoH = lerp(vp.h, videoFinalH, eased);
  const videoRadius = eased * 8;

  // Distance between adjacent photo centres. On mobile step is sized so the
  // side columns push past the viewport edges. On desktop step scales with
  // viewport width but is bounded so the ±2 outer columns still fit on
  // narrower MacBook widths (1280–1440).
  const step = isMobile
    ? Math.max(photoSize + 90, vp.w * 0.46)
    : Math.max(photoSize * 2.4, vp.w * 0.20);

  const photoOffsets = isMobile ? photoOffsetsMobile : photoOffsetsDesktop;

  // Title color flips white → dark earlier than the video shrink.
  const colorT = useMemo(
    () => easeOutCubic(clamp01((progress - 0.04) / 0.18)),
    [progress],
  );
  const titleColor = useMemo(() => {
    const r = Math.round(lerp(250, 17, colorT));
    const g = Math.round(lerp(250, 17, colorT));
    const b = Math.round(lerp(247, 17, colorT));
    return `rgb(${r}, ${g}, ${b})`;
  }, [colorT]);
  const titleShadow = `0 2px 24px rgba(0,0,0,${(1 - colorT) * 0.45})`;

  // Photos / caption / button reach full opacity by the time the video has
  // finished shrinking (end of phase 1). They then fade out only during
  // phase 3 (and only the wall — the caption itself stays visible).
  const captionOpacity = clamp01((progress - 0.05) / 0.20);
  const photosOpacity = captionOpacity * (1 - easeOutCubic(phase3));

  // Phase 2 exit: photos & video scroll up faster than the caption, each
  // column group at its own speed. Phase 3 adds an extra upward shift so
  // they continue moving while the caption rises further.
  // Mobile scales everything down so things don't shoot off the screen.
  const exitScale = isMobile ? 0.55 : 1;
  // On mobile, match the caption's easeOutCubic so photos/video and the
  // caption move in sync (linear phase2 made the caption "leap" past them).
  const phase2Exit = isMobile ? easeOutCubic(phase2) : phase2;
  const phase3Lift = easeOutCubic(phase3) * vp.h * 0.35 * exitScale;
  const photoExitOuter = -phase2Exit * vp.h * 0.50 * exitScale - phase3Lift;  // cols 1 & 5 (|gx| = 2)
  const photoExitInner = -phase2Exit * vp.h * 0.72 * exitScale - phase3Lift;  // cols 2 & 4 (|gx| = 1)
  const photoExitCenter = -phase2Exit * vp.h * 0.60 * exitScale - phase3Lift; // col 3 (gx = 0)
  const videoExitY = -phase2Exit * vp.h * 0.65 * exitScale - phase3Lift;

  // Title rises only halfway up (not off-screen) and fades quickly.
  // Bottom-anchored: titleBottom is the gap (in px) between the title's
  // bottom edge and the viewport bottom — independent of title height.
  // Desktop: 70px above viewport bottom.
  // Mobile: 60px above the sticky-CTA panel top.
  const stickyCtaPanel = 85;
  const titleBottomInit = isMobile ? 60 + stickyCtaPanel : 70;
  const titleBottomFinal = vp.h * 0.5;

  const titleEase = easeOutCubic(clamp01(progress / 0.30));
  const titleBottom = titleBottomInit; // stay in place, only opacity changes
  const titleOpacity = clamp01(1 - titleEase * 1.4);

  // Caption mirrors the photo entrance during phase 1: starts shifted away
  // from its CSS position (same 2.4× expansion) and eases back to 0.
  // In phase 2 it drifts up to the viewport centre. In phase 3 it continues
  // up into the upper area.
  const captionTopRatio = isMobile ? 0.80 : 0.82;
  const captionOffsetFromCenter = (captionTopRatio - 0.5) * vp.h;
  const captionExpansion = 2.4;
  const captionInitOffset =
    captionOffsetFromCenter * captionExpansion - captionOffsetFromCenter;
  const captionPhase1Y = lerp(captionInitOffset, 0, eased);
  const captionPhase2Y = -easeOutCubic(phase2) * captionOffsetFromCenter;
  const captionPhase3Y = -easeOutCubic(phase3) * (vp.h * 0.25);
  const captionY = captionPhase1Y + captionPhase2Y + captionPhase3Y;

  // Book reveal during phase 3 — slides in from below at constant size and
  // rotates from a 3D pose (spine + bottom pages visible) to flat-on by
  // the end. On mobile, both the book's size and final centre are clamped
  // so it always keeps a min gap from the caption above and the sticky CTA
  // panel below — even on short screens (iPhone SE etc.).
  const bookEndRatio = isMobile ? 0.60 : 0.62;
  const bookEase = easeOutCubic(phase3);

  const BOOK_AR = 1654 / 1110; // BookCover height / width
  const MIN_GAP = 16;
  const captionHeightEst = isMobile ? 150 : 110;
  const captionFinalBottom = vp.h * 0.25 + captionHeightEst; // after phase3 rise
  const ctaPanelTop = vp.h - stickyCtaPanel;

  const cssBookWidth = isMobile
    ? Math.max(110, Math.min(180, Math.min(vp.w * 0.36, vp.h * 0.22)))
    : Math.max(160, Math.min(220, vp.w * 0.16));
  const availableH = Math.max(120, ctaPanelTop - MIN_GAP * 2 - captionFinalBottom);
  const bookWidth = isMobile
    ? Math.min(cssBookWidth, availableH / BOOK_AR)
    : cssBookWidth;
  const bookHeight = bookWidth * BOOK_AR;

  const bookCenterIdeal = captionFinalBottom + MIN_GAP + bookHeight / 2;
  const bookCenterMax = ctaPanelTop - MIN_GAP - bookHeight / 2;
  const bookCenterTarget = isMobile
    ? Math.min(Math.max(vp.h * bookEndRatio, bookCenterIdeal), bookCenterMax)
    : vp.h * bookEndRatio;
  const bookY = lerp(vp.h * 1.05, bookCenterTarget, bookEase);
  const bookRotateY = (1 - bookEase) * 60; // 60° → 0°
  const bookRotateX = (1 - bookEase) * 32; // 32° → 0°
  const bookOpacity = clamp01((progress - 0.50) / 0.012);

  // Phase 4 — cycle through the cover examples one by one as the user
  // continues scrolling. Each cover gets an equal slice of phase 4.
  // Cycle the hero book's cover image during phase 4 (title/author are
  // a single localised pair so they don't flicker as the image swaps).
  const heroCoverIdx = Math.min(
    bookCovers.length - 1,
    Math.floor(phase4 * bookCovers.length),
  );
  const heroCover = bookCovers[heroCoverIdx];

  // On `ask.lovestoria.org` this build is a landing-only mirror — no
  // checkout, no /forms. Order CTAs open the Telegram channel (per the
  // mirror's spec — see ARCHITECTURE.md), and links that would normally
  // route inside the app point at the main domain instead.
  const askVariant = isAskVariant();
  const loginHref = askVariant ? mainAppUrl('/login') : '/login';
  const termsPrivacyHref = askVariant ? mainAppUrl('/terms#privacy') : '/terms#privacy';
  const termsOfferHref = askVariant ? mainAppUrl('/terms#offer') : '/terms#offer';

  const goCheckout = (source: string) => {
    if (askVariant) {
      // На ask любой order-CTA уводит прямо к менеджеру — считаем лидом.
      trackLead(source);
      window.open(TELEGRAM_LINK, '_blank', 'noopener,noreferrer');
      return;
    }
    setCheckoutOpen(true);
  };

  return (
    <main className="lp">
      <header className="lp-top">
        {/* Announcement bar — временно скрыт. Раскомментировать чтобы вернуть.
        {showAnno && (
          <div className="lp-anno">
            <div className="lp-anno__inner">
              <p className="lp-anno__text">
                {t('anno.promo')} <strong>{t('anno.promoCode')}</strong>{' '}
                {t('anno.promoDiscount')}{' '}
                <button
                  type="button"
                  className="lp-anno__cta"
                  onClick={() => goCheckout('anno_promo')}
                >
                  {t('anno.use')}
                  <svg
                    width="9"
                    height="9"
                    viewBox="0 0 11 11"
                    aria-hidden="true"
                  >
                    <path
                      d="M2.5 1h7v7M9.5 1L1 9.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </p>
              <button
                type="button"
                className="lp-anno__close"
                aria-label={t('anno.close')}
                onClick={() => setShowAnno(false)}
              >
                <svg
                  width="11"
                  height="11"
                  viewBox="0 0 12 12"
                  aria-hidden="true"
                >
                  <path
                    d="M1 1l10 10M11 1L1 11"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        )}
        */}

        <nav className="lp-nav" ref={navRef}>
          <a className="lp-nav__logo" href={langPrefix}>lovestoria</a>
          <div className="lp-nav__right">
            <LangPicker />
            <button
              type="button"
              className="lp-nav__burger"
              aria-label={t('nav.menu')}
              aria-expanded={menuOpen}
              aria-haspopup="menu"
              onClick={() => setMenuOpen((o) => !o)}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                aria-hidden="true"
              >
                <path
                  d="M2 5h16M2 10h16M2 15h16"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>

          {menuOpen && (
            <div className="lp-nav__menu" role="menu">
              <button
                type="button"
                role="menuitem"
                className="lp-nav__menu-item"
                onClick={() => { setMenuOpen(false); goCheckout('nav_menu_order'); }}
              >
                {t('nav.menuOrder')}
              </button>
              <a
                role="menuitem"
                className="lp-nav__menu-item"
                href={loginHref}
                onClick={() => setMenuOpen(false)}
              >
                {t('nav.menuLogin')}
              </a>
              <a
                role="menuitem"
                className="lp-nav__menu-item"
                href={TELEGRAM_LINK}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => { trackLead('nav_menu_contact'); setMenuOpen(false); }}
              >
                {t('nav.menuContact')}
              </a>
            </div>
          )}
        </nav>
      </header>

      <div className="lp-stickycta">
        <a
          className="lp-stickycta__btn lp-stickycta__btn--tg"
          href={TELEGRAM_LINK}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackLead('sticky_telegram')}
        >
          <TelegramIcon />
          <span>{t('stickycta.order')}</span>
        </a>
      </div>

      <section ref={heroRef} className="lp-hero">
        <div className="lp-hero__sticky">
          <div className="lp-hero__stage">
            {photoOffsets.map(([gx, gy], i) => {
              const dx = gx * step;
              const dy = gy * step;
              const expansion = 2.4;
              const x = lerp(dx * expansion, dx, eased);
              const baseY = lerp(dy * expansion, dy, eased);
              const colGroup = Math.abs(gx);
              const photoExitY =
                colGroup >= 2
                  ? photoExitOuter
                  : colGroup === 1
                    ? photoExitInner
                    : photoExitCenter;
              const y = baseY + photoExitY;
              const doodle = photoDoodles[i % photoDoodles.length];
              return (
                <div
                  key={i}
                  className="lp-hero__photo"
                  style={{
                    width: photoSize,
                    height: photoSize,
                    backgroundImage: `url(${peopleImages[i]})`,
                    transform: `translate3d(calc(50vw + ${x}px - 50%), calc(50vh + ${y}px - 50%), 0)`,
                    opacity: photosOpacity,
                  }}
                >
                  <div className={`lp-hero__doodle lp-hero__doodle--${doodle.placement}`}>
                    <img
                      className="lp-hero__doodle-arrow"
                      src={doodleArrow}
                      alt=""
                      aria-hidden="true"
                    />
                    <span
                      className="lp-hero__doodle-label"
                      style={{ transform: `rotate(${doodle.rotate}deg)` }}
                    >
                      {t(`doodles.${doodle.doodleKey}`)}
                    </span>
                  </div>
                </div>
              );
            })}

            <video
              className="lp-hero__video"
              style={{
                width: videoW,
                height: videoH,
                borderRadius: videoRadius,
                transform: `translate(-50%, calc(-50% + ${videoExitY}px))`,
              }}
              autoPlay
              muted
              loop
              playsInline
              poster={heroVideoPoster}
              preload="metadata"
              src={heroVideo}
            />

            <div
              className="lp-hero__title-wrap"
              style={{
                transform: `translate3d(0, ${-titleBottom}px, 0)`,
                color: titleColor,
                textShadow: titleShadow,
                opacity: titleOpacity,
              }}
            >
              <h1 className="lp-hero__title">{t('hero.title')}</h1>
              <p className="lp-hero__subtitle">{t('hero.subtitle')}</p>
            </div>

            <div
              className="lp-hero__caption"
              style={{
                transform: `translate3d(0, ${captionY}px, 0)`,
                opacity: captionOpacity,
              }}
            >
              <p className="lp-hero__caption-text">{t('hero.caption')}</p>
              <button
                type="button"
                className="lp-hero__caption-cta"
                onClick={() => goCheckout('hero_cta')}
              >
                {t('hero.cta')}
              </button>
            </div>

            <div
              className="lp-hero__book-rise"
              style={{
                width: `${bookWidth}px`,
                transform: `translate3d(-50%, calc(${bookY}px - 50%), 0)`,
                opacity: bookOpacity,
                ['--book-roty' as any]: `${bookRotateY}deg`,
                ['--book-rotx' as any]: `${bookRotateX}deg`,
              }}
            >
              <BookCover
                frozen
                background={heroCover}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="lp-info">
        <div className="lp-info__inner">
          <div className="lp-info__panel">
            <h2 className="lp-info__name">{t('info.name')}</h2>

            <div className="lp-info__meta">
              <div className="lp-info__rating">
                <span className="lp-info__stars" aria-label={t('info.ratingAria')}>
                  {[0, 1, 2, 3, 4].map((i) => (
                    <svg
                      key={i}
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path d="M12 2l2.39 7.36H22l-6.18 4.49L18.21 22 12 17.27 5.79 22l2.39-8.15L2 9.36h7.61z" />
                    </svg>
                  ))}
                </span>
                <span className="lp-info__reviews">{t('info.reviewsCount')}</span>
              </div>
            </div>

            <p className="lp-info__desc">{t('info.desc')}</p>

            <ul className="lp-info__points">
              <li>
                <span className="lp-info__point-num">01</span>
                <span>{t('info.p1')}</span>
              </li>
              <li>
                <span className="lp-info__point-num">02</span>
                <span>{t('info.p2')}</span>
              </li>
              <li>
                <span className="lp-info__point-num">03</span>
                <span>
                  <strong>{t('info.p3num')}</strong> {t('info.p3rest')}
                </span>
              </li>
            </ul>

            <div className="lp-info__cta-stack">
              <a
                href={TELEGRAM_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="lp-info__cta-primary"
                onClick={() => trackLead('info_telegram')}
              >
                <TelegramIcon />
                <span>{t('info.ctaPrimary')}</span>
              </a>
            </div>
          </div>

          <div className="lp-info__cover">
            <img
              className="lp-info__cover-img"
              src={bookImage}
              alt={t('info.name')}
              draggable={false}
            />
          </div>
        </div>
      </section>

      <section className="lp-how">
        <div className="lp-how__inner">
          <header className="lp-how__head">
            <h2 className="lp-how__title">{t('how.title')}</h2>
          </header>

          <ol className="lp-how__steps">
            <li className="lp-how__step">
              <div className="lp-how__step-text">
                <span className="lp-how__num">01</span>
                <h3 className="lp-how__step-title">{t('how.step1Title')}</h3>
                <p className="lp-how__step-body">{t('how.step1Body')}</p>
              </div>

              <div className="lp-how__asset lp-how__asset--cabinet">
                <CabinetPreviewFrame scenario="answers" lang="ru-ru" title={t('how.step1Title')} />
              </div>
            </li>

            <li className="lp-how__step">
              <div className="lp-how__step-text">
                <span className="lp-how__num">02</span>
                <h3 className="lp-how__step-title">{t('how.step2Title')}</h3>
                <p className="lp-how__step-body">{t('how.step2Body')}</p>
              </div>

              <div className="lp-how__asset lp-how__asset--cabinet">
                <CabinetPreviewFrame scenario="cover" lang="ru-ru" title={t('how.step2Title')} />
              </div>
            </li>

            <li className="lp-how__step">
              <div className="lp-how__step-text">
                <span className="lp-how__num">03</span>
                <h3 className="lp-how__step-title">{t('how.step3Title')}</h3>
                <p className="lp-how__step-body">{t('how.step3Body')}</p>
              </div>

              <div className="lp-how__asset lp-how__asset--photo">
                <img src={p11} alt="" />
              </div>
            </li>
          </ol>

          <div className="lp-how__timing">
            <h3 className="lp-how__timing-title">{t('how.timingTitle')}</h3>
            <ul className="lp-how__timing-list">
              <li>
                <span className="lp-how__timing-label">{t('how.timingEdit')}</span>
                <span className="lp-how__timing-value">{t('how.timingEditValue')}</span>
              </li>
              <li>
                <span className="lp-how__timing-label">{t('how.timingDelivery')}</span>
                <span className="lp-how__timing-value">{t('how.timingDeliveryValue')}</span>
              </li>
            </ul>
            <div className="lp-how__timing-total">
              <span>{t('how.timingTotal')}</span>
              <span className="lp-how__timing-value">{t('how.timingTotalValue')}</span>
            </div>
            <p className="lp-how__timing-note">{t('how.timingNote')}</p>
          </div>
        </div>
      </section>

      <section className="lp-reviews">
        <header className="lp-reviews__head">
          <h2 className="lp-reviews__title">{t('reviews.title')}</h2>
          <p className="lp-reviews__subtitle">{t('reviews.subtitle')}</p>
        </header>

        <div className="lp-reviews__rail">
          <div className="lp-reviews__track">
            {[...reviewSlots, ...reviewSlots].map((r, i) =>
              r.kind === 'photo' ? (
                <article key={i} className="lp-rev lp-rev--photo">
                  <img
                    className="lp-rev__photo"
                    src={r.poster}
                    alt=""
                    loading="lazy"
                    draggable={false}
                  />
                </article>
              ) : (
                <article key={i} className="lp-rev lp-rev--text">
                  <p className="lp-rev__quote">«{t(r.quoteKey)}»</p>
                  <p className="lp-rev__by">— {t(r.byKey)}</p>
                </article>
              )
            )}
          </div>
        </div>
      </section>

      <section className="lp-cta">
        <div className="lp-cta__inner">
          <div className="lp-cta__head">
            <h2 className="lp-cta__title">
              {t('cta.titlePrefix')}<br />
              <span className="lp-cta__cycle" key={`w${occIdx}`}>
                {t(ctaOccasionKeys[occIdx].wordKey)}
              </span>
            </h2>
            <div className="lp-cta__msg" key={`m${occIdx}`}>
              {t(ctaOccasionKeys[occIdx].msgKey)}
            </div>
          </div>
          <div className="lp-cta__actions">
            <a
              href={TELEGRAM_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="lp-cta__btn"
              onClick={() => trackLead('cta_telegram')}
            >
              <TelegramIcon />
              <span>{t('cta.btn')}</span>
            </a>
            <p className="lp-cta__note">{t('cta.note')}</p>
          </div>
        </div>
      </section>

      <footer className="lp-footer">
        <div className="lp-footer__inner lp-footer__inner--legal">
          <span className="lp-footer__copy">{t('footer.copyright')}</span>
          <nav className="lp-footer__legal-links">
            <a href={termsPrivacyHref}>{t('footer.legalPrivacy')}</a>
            <a href={termsOfferHref}>{t('footer.legalTerms')}</a>
          </nav>
        </div>
      </footer>

      {!askVariant && (
        <CheckoutSidebar
          open={checkoutOpen}
          onClose={() => setCheckoutOpen(false)}
          defaultCurrency={defaultCurrency}
        />
      )}
    </main>
  );
};

export default Landing;
